import type { DAGData, DAGEdge, DAGNode } from "../../api/artifacts";
import type {
  DagDataChunk,
  DagPathState,
  DagRecordType,
  DagSemanticRole,
  DagViewerEdge,
  DagViewerGraph,
  DagViewerNode,
} from "./types";

function stringValue(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return null;
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function boolValue(...values: unknown[]): boolean {
  return values.some((value) => value === true || value === "true" || value === 1);
}

function recordType(
  raw: DAGNode,
  metadata: Record<string, unknown>,
  valueJson: Record<string, unknown>,
  kind: string,
): DagRecordType {
  if (stringValue(
    raw.operation_kind,
    metadata.operation_kind,
    valueJson.operation_kind,
    metadata.operation_spec_id,
    valueJson.operation_spec_id,
  )) return "operation";
  const hint = stringValue(raw.node_type, raw.record_type, metadata.node_type, metadata.record_type, raw.type, kind)
    ?.toLowerCase() ?? "artifact";
  if (/lifecycle|invalidation|supersed/.test(hint)) return "lifecycle";
  if (/decision|review|approval/.test(hint)) return "decision";
  if (/operation|op_run|activity|execution/.test(hint)) return "operation";
  // The V3 DAG projection intentionally exposes the record taxonomy as `kind`.
  // Operation spec kinds are action-qualified (`mesh.generate`, `plot.render`,
  // `record.publish`), while ArtifactVersion kinds are nouns.
  if (/^[a-z0-9_-]+\.[a-z0-9_-]+$/i.test(kind)) return "operation";
  if (/human|message|meeting|note|comment|slack|email|spool/.test(hint)) return "human";
  if (/version|revision/.test(hint)) return "version";
  return "artifact";
}

function pathState(type: DagRecordType, state: string | null, anchor: boolean): DagPathState {
  const normalized = state?.toLowerCase() ?? "";
  if (/invalid|stale|reject|supersed|failed|error|deleted/.test(normalized)) return "invalidated";
  if (type === "human" || anchor) return "human";
  if (/active|valid|complete|success|current|ready|published/.test(normalized)) return "active";
  return "context";
}

function semanticRole(
  raw: DAGNode,
  metadata: Record<string, unknown>,
  kind: string,
  type: DagRecordType,
): DagSemanticRole {
  const declared = stringValue(raw.semantic_role, metadata.semantic_role)?.toLowerCase();
  const exact = declared ?? kind.toLowerCase();
  if (metadata.boundary === true || exact === "boundary") return "boundary";
  if (exact === "journal" || exact === "journal_entry") return "journal";
  if (exact === "decision_evidence") return "decision_evidence";
  if (exact === "decision_status" || exact === "decision_status_event") return "decision_status";
  if (exact === "decision") return "decision";
  if (exact === "actor") return "actor";
  if (exact === "artifact_version") return "artifact_version";
  if (exact === "artifact") return "artifact";
  if (exact === "unknown" || exact === "unsupported") return "unknown";
  if (type === "operation") return "operation";
  if (type === "human") return "human";
  if (type === "decision") return "decision";
  if (type === "lifecycle") return "lifecycle";
  return type === "version" ? "artifact_version" : "artifact";
}

function normalizeNode(raw: DAGNode): DagViewerNode {
  const metadata = objectValue(raw.metadata);
  const valueJson = objectValue(raw.value_json);
  const kind = stringValue(raw.kind, raw.record_type, metadata.record_type, raw.type) ?? "artifact";
  const type = recordType(raw, metadata, valueJson, kind);
  const exactSemanticRole = semanticRole(raw, metadata, kind, type);
  const path = stringValue(raw.path) ?? "";
  const pathParts = path.split(".").filter(Boolean);
  const pathTail = pathParts[pathParts.length - 1] ?? "";
  const rawName = stringValue(raw.name);
  const label = stringValue(
    raw.label,
    metadata.label,
    metadata.title,
    rawName && rawName !== kind ? rawName : null,
    metadata.name,
    type === "operation" ? kind : null,
    pathTail && pathTail !== kind ? pathTail : null,
    kind,
    raw.id,
  ) ?? raw.id;
  const timestamp = stringValue(
    raw.started_at,
    metadata.started_at,
    valueJson.started_at,
    raw.completed_at,
    metadata.completed_at,
    valueJson.completed_at,
    raw.created_at,
    raw.updated_at,
    raw.timestamp,
    raw.t,
    metadata.created_at,
    metadata.timestamp,
    metadata.source_created_at,
  );
  const stale = raw.is_stale === true || metadata.is_stale === true;
  const inferredState = /invalid|reject|stale|supersed/.test(kind.toLowerCase()) ? "invalidated" : null;
  const state = stale
    ? "invalidated"
    : stringValue(raw.lifecycle_state, raw.state, raw.status, metadata.lifecycle_state, metadata.state, metadata.status, inferredState);
  const digest = stringValue(
    raw.digest,
    raw.content_digest,
    raw.checksum,
    raw.sha256,
    metadata.digest,
    metadata.content_digest,
    metadata.checksum,
    metadata.representation_sha256,
    metadata.png_sha256,
    metadata.snapshot_hash,
    valueJson.digest,
    valueJson.representation_sha256,
    valueJson.png_sha256,
    valueJson.snapshot_hash,
  );
  const anchor = boolValue(raw.anchor, raw.story_anchor, metadata.anchor, metadata.story_anchor);
  const channel = stringValue(
    raw.channel,
    metadata.channel,
    metadata.source_channel,
    metadata.actor_name,
    metadata.actor,
    raw.created_by,
  );

  return {
    id: String(raw.id),
    label,
    kind,
    type,
    semanticRole: exactSemanticRole,
    state,
    timestamp,
    digest,
    channel,
    anchor,
    pathState: pathState(type, state, anchor),
    metadata,
    raw,
  };
}

function edgeEndpoint(raw: DAGEdge, side: "source" | "target"): string {
  const value = side === "source"
    ? raw.parent_id ?? raw.source ?? raw.s
    : raw.child_id ?? raw.target ?? raw.d;
  return String(value ?? "");
}

export function normalizeDagData(data: DAGData): DagViewerGraph {
  const nodes = data.nodes
    .map(normalizeNode)
    .filter((node) => Boolean(node.id))
    .sort((a, b) => a.id.localeCompare(b.id));
  const nodeIndexById = new Map(nodes.map((node, index) => [node.id, index]));

  const edges: DagViewerEdge[] = data.edges
    .map((raw, originalIndex) => {
      const sourceId = edgeEndpoint(raw, "source");
      const targetId = edgeEndpoint(raw, "target");
      const source = nodeIndexById.get(sourceId);
      const target = nodeIndexById.get(targetId);
      if (source == null || target == null || source === target) return null;
      return {
        id: String(raw.id ?? `edge-${sourceId}-${targetId}-${originalIndex}`),
        source,
        target,
        kind: stringValue(raw.relationship, raw.operation, raw.kind, raw.k) ?? "related",
        raw,
      } satisfies DagViewerEdge;
    })
    .filter((edge): edge is DagViewerEdge => edge !== null)
    .sort((a, b) => a.id.localeCompare(b.id) || a.source - b.source || a.target - b.target);

  const incoming = Array.from({ length: nodes.length }, () => [] as number[]);
  const outgoing = Array.from({ length: nodes.length }, () => [] as number[]);
  const incidentEdges = Array.from({ length: nodes.length }, () => [] as number[]);
  edges.forEach((edge, edgeIndex) => {
    outgoing[edge.source]!.push(edge.target);
    incoming[edge.target]!.push(edge.source);
    incidentEdges[edge.source]!.push(edgeIndex);
    incidentEdges[edge.target]!.push(edgeIndex);
  });
  for (const list of [...incoming, ...outgoing]) list.sort((a, b) => a - b);

  return { nodes, edges, nodeIndexById, incoming, outgoing, incidentEdges };
}

/**
 * Chunk-friendly ingestion boundary. The current endpoint returns one payload,
 * while a future NDJSON/SSE transport can append records and call snapshot at
 * a cadence chosen by the surface without changing layout or renderer inputs.
 */
export class DagGraphAccumulator {
  private readonly nodes = new Map<string, DAGNode>();
  private readonly edges = new Map<string, DAGEdge>();

  append(chunk: DagDataChunk): void {
    for (const node of chunk.nodes ?? []) this.nodes.set(String(node.id), node);
    for (const [index, edge] of (chunk.edges ?? []).entries()) {
      const source = edgeEndpoint(edge, "source");
      const target = edgeEndpoint(edge, "target");
      const key = String(edge.id ?? `${source}:${target}:${edge.relationship ?? edge.operation ?? "edge"}:${index}`);
      this.edges.set(key, edge);
    }
  }

  snapshot(): DagViewerGraph {
    return normalizeDagData({ nodes: [...this.nodes.values()], edges: [...this.edges.values()] });
  }

  clear(): void {
    this.nodes.clear();
    this.edges.clear();
  }
}

export function collectRelated(graph: DagViewerGraph, start: number, direction: "incoming" | "outgoing"): Uint8Array {
  const adjacency = direction === "incoming" ? graph.incoming : graph.outgoing;
  const seen = new Uint8Array(graph.nodes.length);
  const queue = [start];
  seen[start] = 1;
  for (let head = 0; head < queue.length; head += 1) {
    const current = queue[head]!;
    for (const next of adjacency[current] ?? []) {
      if (seen[next]) continue;
      seen[next] = 1;
      queue.push(next);
    }
  }
  return seen;
}

export function focusContext(graph: DagViewerGraph, start: number): Uint8Array {
  const ancestors = collectRelated(graph, start, "incoming");
  const descendants = collectRelated(graph, start, "outgoing");
  const context = new Uint8Array(graph.nodes.length);
  for (let index = 0; index < context.length; index += 1) {
    context[index] = ancestors[index] || descendants[index] ? 1 : 0;
  }
  return context;
}

/**
 * Stable induced subgraph used by non-canvas projections. Node objects retain
 * their source identity while adjacency indices are rebuilt for the subset.
 */
export function projectDagGraphByMask(graph: DagViewerGraph, mask: Uint8Array | null): DagViewerGraph {
  if (!mask || mask.length !== graph.nodes.length) return graph;
  const sourceIndices: number[] = [];
  for (let index = 0; index < graph.nodes.length; index += 1) {
    if (mask[index]) sourceIndices.push(index);
  }
  const sourceToProjected = new Int32Array(graph.nodes.length);
  sourceToProjected.fill(-1);
  sourceIndices.forEach((sourceIndex, projectedIndex) => { sourceToProjected[sourceIndex] = projectedIndex; });
  const nodes = sourceIndices.map((index) => graph.nodes[index]!);
  const edges = graph.edges.flatMap((edge) => {
    const source = sourceToProjected[edge.source]!;
    const target = sourceToProjected[edge.target]!;
    return source < 0 || target < 0 ? [] : [{ ...edge, source, target }];
  });
  const nodeIndexById = new Map(nodes.map((node, index) => [node.id, index]));
  const incoming = Array.from({ length: nodes.length }, () => [] as number[]);
  const outgoing = Array.from({ length: nodes.length }, () => [] as number[]);
  const incidentEdges = Array.from({ length: nodes.length }, () => [] as number[]);
  edges.forEach((edge, edgeIndex) => {
    outgoing[edge.source]!.push(edge.target);
    incoming[edge.target]!.push(edge.source);
    incidentEdges[edge.source]!.push(edgeIndex);
    incidentEdges[edge.target]!.push(edgeIndex);
  });
  for (const adjacency of [...incoming, ...outgoing]) adjacency.sort((a, b) => a - b);
  return { nodes, edges, nodeIndexById, incoming, outgoing, incidentEdges };
}
